import React from "react";
import { Table, Typography, TableColumnsType } from "antd";
import { formatCurrency } from "./quoteVersionUtils";
import { Skeleton } from "../../../components/Skeleton";

interface PricingSummaryTableProps {
  subtotal: number | null;
  shipping: number | null;
  tax: number | null;
  additionalDiscount: number | null;
  additionalDiscountNote: string | null | undefined;
  orderAmount: number | null;
  amountPaid: number | null;
  listSubtotal: number | null;
  isOrder?: boolean;
  // While true, values are still catching up with the debounced live recalc preview — show
  // a lightweight loading affordance instead of (possibly stale) numbers, same Skeleton
  // convention already used in HardwareReplacementModal.
  previewLoading?: boolean;
}

interface PricingDataType {
  key: string;
  pricingSummary: string;
  listPrice: number | null;
  netPrice: number | null;
}

export const PricingSummaryTable: React.FC<PricingSummaryTableProps> = ({
  subtotal,
  shipping,
  tax,
  additionalDiscount,
  additionalDiscountNote,
  orderAmount,
  amountPaid,
  listSubtotal,
  isOrder,
  previewLoading,
}) => {
  const renderPrice = (price: number | null) =>
    previewLoading ? (
      <Skeleton width="60px" height="16px" style={{ marginLeft: "auto" }} />
    ) : (
      formatCurrency(price)
    );

  const pricingSummaryColumns: TableColumnsType<PricingDataType>= [
    {
      title: "Pricing Summary",
      dataIndex: "pricingSummary",
      key: "pricingSummary",
    },
    {
      title: "Net Price",
      dataIndex: "netPrice",
      key: "netPrice",
      align: "right",
      width: 110,
      render: (price: number | null) => renderPrice(price),
    },
  ];

  const pricingItems = [
    ...(additionalDiscount
      ? [
          {
            key: "additionalDiscount",
            pricingSummary: additionalDiscountNote
              ? `Additional Discount (${additionalDiscountNote})`
              : "Additional Discount",
            listPrice: null,
            netPrice: -additionalDiscount,
          },
        ]
      : []),
    {
      key: "subtotal",
      pricingSummary: "Subtotal",
      listPrice: listSubtotal,
      netPrice: subtotal,
    },
    {
      key: "shipping",
      pricingSummary: shipping
        ? "Est. Frt (COD is Add'l)"
        : "Freight Not Included (Add'l)",
      listPrice: null,
      netPrice: shipping,
    },
    {
      key: "tax",
      pricingSummary: "Tax (Additional)",
      listPrice: null,
      netPrice: tax,
    },
  ];

  // Mirrors the BP estimate report: quotes show pricing through the order
  // total only; orders additionally show deposit received (amount paid)
  // and amount due = total - paid.
  const amountDue = (orderAmount ?? 0) - (amountPaid ?? 0);

  const pricingSummaryRows = () => (
    <>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}>
          <Typography.Text strong>
            Total Order (Not Incl COD Chrg)
          </Typography.Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <Typography.Text strong>
            {renderPrice(orderAmount)}
          </Typography.Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
      {isOrder && (
        <>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography.Text>Deposit Received</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Typography.Text>
                -{formatCurrency(amountPaid ?? 0)}
              </Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography.Text strong>Amount Due</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Typography.Text strong>
                {previewLoading ? (
                  <Skeleton width="60px" height="16px" style={{ marginLeft: "auto" }} />
                ) : (
                  formatCurrency(amountDue)
                )}
              </Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </>
      )}
    </>
  );

  return (
    <Table
      size="small"
      bordered
      pagination={false}
      dataSource={pricingItems}
      columns={pricingSummaryColumns}
      summary={pricingSummaryRows}
      style={{ marginBottom: 12 }}
    />
  );
};
